import PageHeader from "@/components/PageHeader";
import { SECRETARY_ROUTE_GUARD_TRANSLATIONS } from "@/components/secretaryRouteGuardConstants";
import { Button } from "@/domains/shared/components/Button";
import { useAuth } from "@/domains/shared/hooks/useAuth";
import { Roles } from "@/domains/shared/models/roles";
import {
    hasSecretaryPermission,
    secretaryHasFinanceMenu,
} from "@/domains/shared/services/secretaryPermissions";
import {
    clinicSecretaryHasFinanceMenu,
    clinicSecretaryHasPermission,
} from "@/domains/multi/clinicSecretaryPermissions";
import { getTranslation } from "@mivo/i18n";
import type { ReactNode } from "react";
import { Navigate, useNavigate } from "react-router";

type SecretaryRouteGuardProps = {
    children: ReactNode;
    /** Dot path, e.g. ["agenda", "view"] */
    permission?: string[];
    requireFinanceMenu?: boolean;
};

export default function SecretaryRouteGuard({
    children,
    permission,
    requireFinanceMenu = false,
}: SecretaryRouteGuardProps) {
    const { user } = useAuth();
    const navigate = useNavigate();

    if (user?.role !== Roles.Secretary) {
        return <>{children}</>;
    }

    if (user.secretaryAccessBlocked) {
        return <Navigate to="/administrativo" replace />;
    }

    const allowed = user.isClinicSecretary
        ? requireFinanceMenu
            ? clinicSecretaryHasFinanceMenu()
            : permission
                ? clinicSecretaryHasPermission(...permission)
                : true
        : requireFinanceMenu
        ? secretaryHasFinanceMenu(user.secretaryPermissions)
        : permission
            ? hasSecretaryPermission(user.secretaryPermissions, ...permission)
            : true;

    if (!allowed) {
        return (
            <div className="p-6">
                <PageHeader
                    title={getTranslation(SECRETARY_ROUTE_GUARD_TRANSLATIONS.title)}
                    subtitle={getTranslation(SECRETARY_ROUTE_GUARD_TRANSLATIONS.subtitle)}
                />
                <Button type="button" onClick={() => navigate("/administrativo")}>
                    {getTranslation(SECRETARY_ROUTE_GUARD_TRANSLATIONS.backHome)}
                </Button>
            </div>
        );
    }

    return <>{children}</>;
}
